import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useCreateNotification, useUpdateNotification, useNotificationTypes,
  useTestNotification, useTestNotificationConfig,
} from '../../hooks/useNotifications';
import { Alert } from '../common/Alert';
import { Button } from '../common/Button';
import { Field } from '../common/Field';
import { Input, Select, Textarea, checkboxClass } from '../common/Input';
import { useToast } from '../common/Toast';
import { apiError } from '../../utils/apiError';
import { parseHeaders } from '../../utils/headers';
import { NotificationChannel, NotificationChannelInput, NotificationEvent, NotificationTarget, NotificationTestResult, NOTIFICATION_EVENTS } from '../../types';
import { PROVIDERS, providerByType, defaultConfig, FieldSpec } from './providers';
import { TargetPicker } from './TargetPicker';

const MASK = '••••••••';

/** What the API sends back in place of a stored credential. */
function isMaskedValue(v: unknown): boolean {
  return v === MASK || (typeof v === 'string' && v.endsWith('/…'));
}

export const EVENT_HELP: Record<NotificationEvent, string> = {
  down: 'A service is confirmed down',
  degraded: 'A service is confirmed degraded (slow, or partially failing)',
  recovery: 'A service is back up',
  reminder: 'An incident is still open and unacknowledged (repeats on REMINDER_INTERVAL_MINUTES)',
  escalation: 'An incident was not acknowledged within ESCALATION_AFTER_MINUTES',
  maintenance: 'A maintenance window starts or ends',
  cert_expiring: "An HTTPS target's TLS certificate is inside its expiry threshold (CERT_EXPIRY_DAYS or the service's own); repeats daily until renewed",
  heartbeat_missed: 'A heartbeat service stopped pinging (channels subscribed to "down" receive these too)',
};

interface Props {
  channel?: NotificationChannel;
  onClose: () => void;
}

/** Field values as strings for the inputs; converted to the config shape on submit. */
type Draft = Record<string, string | boolean>;

function draftFromConfig(type: string, config: Record<string, unknown> | undefined): Draft {
  const spec = providerByType.get(type);
  const source = config ?? defaultConfig(type);
  const draft: Draft = {};
  for (const f of spec?.fields ?? []) {
    const v = source[f.key];
    if (f.kind === 'checkbox') { draft[f.key] = Boolean(v); continue; }
    if (f.kind === 'headers') { draft[f.key] = v && typeof v === 'object' ? JSON.stringify(v, null, 2) : '{}'; continue; }
    if (f.kind === 'numberlist') { draft[f.key] = Array.isArray(v) ? v.join(', ') : ''; continue; }
    // Credentials never round-trip through the input: blank means "keep".
    if (f.secret && isMaskedValue(v)) { draft[f.key] = ''; continue; }
    draft[f.key] = v === undefined || v === null ? '' : String(v);
  }
  return draft;
}

export function NotificationForm({ channel, onClose }: Props) {
  const isEdit = Boolean(channel);
  const create = useCreateNotification();
  const update = useUpdateNotification();
  const testSaved = useTestNotification();
  const testConfig = useTestNotificationConfig();
  const { data: serverTypes } = useNotificationTypes();
  const { addToast } = useToast();

  const [type, setType] = useState(channel?.type ?? 'slack');
  const [name, setName] = useState(channel?.name ?? '');
  const [events, setEvents] = useState<NotificationEvent[]>(channel?.events ?? ['down', 'recovery']);
  const [targets, setTargets] = useState<NotificationTarget[]>(channel?.targets ?? []);
  const [enabled, setEnabled] = useState(channel?.enabled ?? true);
  const [draft, setDraft] = useState<Draft>(() => draftFromConfig(type, channel?.config));
  /** Optional secrets the user asked to remove on this edit. */
  const [cleared, setCleared] = useState<Set<string>>(new Set());
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [rawConfig, setRawConfig] = useState('');
  const [testResult, setTestResult] = useState<NotificationTestResult | null>(null);
  const [configTouched, setConfigTouched] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  // Put the cursor on the first problem once the errors have rendered.
  useEffect(() => {
    if (Object.keys(fieldErrors).length === 0) return;
    formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
  }, [fieldErrors]);

  const spec = providerByType.get(type);
  // Types the server offers, in catalogue order, plus anything it knows that we don't.
  const typeOptions = useMemo(() => {
    const offered = new Set((serverTypes ?? PROVIDERS).map(t => t.type));
    const known = PROVIDERS.filter(p => offered.has(p.type)).map(p => ({ type: p.type, label: p.label }));
    const extra = (serverTypes ?? []).filter(t => !providerByType.has(t.type));
    return [...known, ...extra];
  }, [serverTypes]);

  const storedConfig = channel?.config ?? {};
  const hasStored = (key: string) => isEdit && isMaskedValue(storedConfig[key]);

  const changeType = (next: string) => {
    setType(next);
    setDraft(draftFromConfig(next, undefined));
    setCleared(new Set());
    setFieldErrors({});
    setTestResult(null);
    if (!name && providerByType.get(next)) setName(providerByType.get(next)!.label);
  };

  const setField = (key: string, value: string | boolean) => {
    setDraft(d => ({ ...d, [key]: value }));
    setConfigTouched(true);
    if (fieldErrors[key]) setFieldErrors(e => { const n = { ...e }; delete n[key]; return n; });
  };

  /**
   * Build the config the API wants from the draft. Returns null (and sets
   * field errors) when something on screen cannot be sent.
   */
  const buildConfig = (): Record<string, unknown> | null => {
    if (!spec) {
      try {
        const parsed = JSON.parse(rawConfig || '{}');
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) throw new Error('Config must be a JSON object');
        return parsed as Record<string, unknown>;
      } catch (err) {
        setFieldErrors({ __raw: err instanceof Error ? err.message : 'Invalid JSON' });
        return null;
      }
    }

    const errors: Record<string, string> = {};
    const out: Record<string, unknown> = {};
    for (const f of spec.fields) {
      const v = draft[f.key];
      if (f.kind === 'checkbox') { out[f.key] = Boolean(v); continue; }
      if (f.kind === 'headers') {
        const parsed = parseHeaders(String(v ?? ''));
        if (!parsed.ok) { errors[f.key] = parsed.message; continue; }
        if (parsed.value) out[f.key] = parsed.value;
        continue;
      }
      const text = String(v ?? '').trim();
      if (f.kind === 'numberlist') {
        const parts = text.split(/[,\s]+/).filter(Boolean);
        const nums = parts.map(Number);
        if (nums.some(n => !Number.isInteger(n) || n <= 0)) { errors[f.key] = 'Must be whole numbers, comma-separated'; continue; }
        out[f.key] = nums;
        continue;
      }
      if (f.secret && cleared.has(f.key)) { out[f.key] = null; continue; }
      if (text === '') {
        // A blank secret on edit keeps the stored one; the server resolves it.
        if (f.required && !(f.secret && hasStored(f.key))) errors[f.key] = 'Required';
        continue;
      }
      if (f.kind === 'number') {
        const n = Number(text);
        if (!Number.isFinite(n)) { errors[f.key] = 'Must be a number'; continue; }
        out[f.key] = n;
        continue;
      }
      out[f.key] = text;
    }
    setFieldErrors(errors);
    return Object.keys(errors).length > 0 ? null : out;
  };

  const handleTest = () => {
    setTestResult(null);
    // A saved channel is tested as stored: the server holds the real secrets,
    // and a half-edited form with blanked credentials could not be sent.
    if (isEdit && channel && !configTouched) {
      testSaved.mutate({ id: channel.id }, {
        onSuccess: setTestResult,
        onError: err => addToast('error', apiError(err, 'Could not send the test')),
      });
      return;
    }
    const config = buildConfig();
    if (!config) return;
    // The ad-hoc test cannot see stored secrets, so a blank (or still-masked
    // header) credential would go out literally. Ask for it, or for a save.
    const unentered = isEdit && spec?.fields.some(f => {
      if (!f.secret || cleared.has(f.key)) return false;
      if (f.kind === 'headers') return Object.values((config[f.key] as Record<string, string> | undefined) ?? {}).some(v => v === MASK);
      return hasStored(f.key) && config[f.key] === undefined;
    });
    if (unentered) {
      addToast('info', 'Save first to test with the stored credentials, or re-enter them to test the values on screen.');
      return;
    }
    testConfig.mutate({ type, config }, {
      onSuccess: setTestResult,
      onError: err => addToast('error', apiError(err, 'Could not send the test')),
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const config = buildConfig();
    if (!config) {
      addToast('error', 'Fix the highlighted fields — nothing was saved.');
      return;
    }
    if (events.length === 0) {
      addToast('error', 'Pick at least one event, or the channel would never send anything.');
      return;
    }

    const data: NotificationChannelInput = { type, name, config, events, enabled, targets };
    if (isEdit && channel) {
      update.mutate({ id: channel.id, data }, {
        onSuccess: () => { addToast('success', 'Channel updated'); onClose(); },
        onError: err => addToast('error', apiError(err, 'Failed to update channel')),
      });
    } else {
      create.mutate(data, {
        onSuccess: () => { addToast('success', 'Channel created'); onClose(); },
        onError: err => addToast('error', apiError(err, 'Failed to create channel')),
      });
    }
  };

  const renderField = (f: FieldSpec) => {
    const error = fieldErrors[f.key];
    const stored = hasStored(f.key);
    const removed = cleared.has(f.key);
    const mono = f.mono ? 'font-mono' : '';
    const value = draft[f.key];

    if (f.kind === 'checkbox') {
      return (
        <label key={f.key} className="inline-flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className={`${checkboxClass} mt-0.5`}
            checked={Boolean(value)}
            onChange={e => setField(f.key, e.target.checked)}
          />
          <span>
            {f.label}
            {f.hint && <span className="block text-xs text-gray-500 dark:text-gray-400">{f.hint}</span>}
          </span>
        </label>
      );
    }

    // Credentials: blank keeps the stored value; an optional one can be removed.
    let hint = f.hint;
    if (f.secret && stored && !removed) {
      const shown = typeof storedConfig[f.key] === 'string' && storedConfig[f.key] !== MASK ? ` (${storedConfig[f.key]})` : '';
      hint = `Stored${shown} — leave blank to keep it.${f.hint ? ` ${f.hint}` : ''}`;
    }

    const required = f.required && !(f.secret && stored);

    return (
      <Field key={f.key} label={f.label} required={required} hint={hint} error={error}>
        {p => {
          if (f.kind === 'select') {
            return (
              <Select {...p} value={String(value ?? '')} onChange={e => setField(f.key, e.target.value)}>
                {f.options?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </Select>
            );
          }
          if (f.kind === 'textarea' || f.kind === 'headers') {
            return (
              <Textarea
                {...p}
                rows={f.kind === 'headers' ? 2 : 3}
                className={mono}
                value={String(value ?? '')}
                placeholder={f.placeholder}
                onChange={e => setField(f.key, e.target.value)}
                onBlur={f.kind === 'headers' ? e => {
                  const r = parseHeaders(e.target.value);
                  setFieldErrors(prev => ({ ...prev, ...(r.ok ? {} : { [f.key]: r.message }) }));
                } : undefined}
              />
            );
          }
          return (
            <div className="flex gap-2 items-center">
              <Input
                {...p}
                type={f.kind === 'password' ? 'password' : f.kind === 'number' ? 'number' : f.kind === 'url' ? 'url' : 'text'}
                autoComplete={f.kind === 'password' ? 'new-password' : 'off'}
                className={mono}
                value={String(value ?? '')}
                disabled={removed}
                placeholder={f.secret && stored ? MASK : f.placeholder}
                required={required}
                onChange={e => setField(f.key, e.target.value)}
              />
              {f.secret && stored && !f.required && (
                <label className="inline-flex items-center gap-1.5 text-sm whitespace-nowrap">
                  <input
                    type="checkbox"
                    className={checkboxClass}
                    checked={removed}
                    onChange={e => {
                      setCleared(prev => { const n = new Set(prev); e.target.checked ? n.add(f.key) : n.delete(f.key); return n; });
                      setConfigTouched(true);
                    }}
                  />
                  Remove
                </label>
              )}
            </div>
          );
        }}
      </Field>
    );
  };

  const testing = testSaved.isPending || testConfig.isPending;

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Type" hint={isEdit ? 'Changing the type starts the settings from scratch.' : undefined}>
          {p => (
            <Select {...p} value={type} onChange={e => changeType(e.target.value)}>
              {typeOptions.map(t => <option key={t.type} value={t.type}>{t.label}</option>)}
            </Select>
          )}
        </Field>
        <Field label="Name" required>
          {p => (
            <Input {...p} type="text" required value={name} onChange={e => setName(e.target.value)} placeholder="Ops channel" />
          )}
        </Field>
      </div>

      {spec ? (
        <div className="rounded-md border border-gray-200 dark:border-gray-700 p-4 space-y-4">
          <div>
            <p className="text-sm">{spec.description}</p>
            {spec.help && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{spec.help}</p>}
          </div>
          {spec.fields.map(renderField)}
        </div>
      ) : (
        <Field label="Config (JSON)" error={fieldErrors.__raw} hint="This provider has no form yet; enter its settings as JSON.">
          {p => (
            <Textarea {...p} rows={5} className="font-mono" value={rawConfig} onChange={e => { setRawConfig(e.target.value); setConfigTouched(true); }} />
          )}
        </Field>
      )}

      <fieldset>
        <legend className="block text-sm font-medium mb-1">Send on</legend>
        <div className="grid gap-1.5 sm:grid-cols-2">
          {NOTIFICATION_EVENTS.map(ev => (
            <label key={ev} className="inline-flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className={`${checkboxClass} mt-0.5`}
                checked={events.includes(ev)}
                onChange={e => setEvents(e.target.checked ? [...events, ev] : events.filter(x => x !== ev))}
              />
              <span>
                <span className="capitalize">{ev}</span>
                <span className="block text-xs text-gray-500 dark:text-gray-400">{EVENT_HELP[ev]}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <TargetPicker value={targets} onChange={setTargets} />

      <label className="inline-flex items-center gap-2 text-sm">
        <input type="checkbox" className={checkboxClass} checked={enabled} onChange={e => setEnabled(e.target.checked)} />
        Enabled
      </label>

      <div className="rounded-md border border-gray-200 dark:border-gray-700 p-3 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-sm font-medium">Test</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {isEdit && !configTouched
                ? 'Sends a sample through the saved channel.'
                : 'Sends a sample with the settings on screen. Nothing is saved.'}
            </p>
          </div>
          <Button type="button" variant="secondary" size="sm" loading={testing} onClick={handleTest}>
            {testing ? 'Sending…' : 'Send test'}
          </Button>
        </div>
        {testResult && (
          <Alert tone={testResult.success ? 'success' : 'error'}>
            {testResult.success
              ? `Delivered${testResult.status_code ? ` (HTTP ${testResult.status_code})` : ''}.`
              : `Failed: ${testResult.error ?? (testResult.status_code ? `HTTP ${testResult.status_code}` : 'unknown error')}`}
          </Alert>
        )}
      </div>

      <div className="flex gap-3 pt-1">
        <Button type="submit" loading={create.isPending || update.isPending}>
          {isEdit ? 'Save changes' : 'Create channel'}
        </Button>
        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
      </div>
    </form>
  );
}
